import React, { useEffect } from "react";
import DatePicker from "react-datepicker";
import AsyncSelect from "react-select/lib/Async";
import LoadMask from "Utils/LoadMask/LoadMask";
import { Controller, useForm } from "react-hook-form";
import { CreacionEVSApi } from "../api/CreacionEVSApi";
import {
    construirOpcionesConsumidorFinal,
    TIPO_CONSUMIDOR_FINAL_ESPECIFICO,
} from "../helpers/opcionesConsumidorFinal";
import { useDebouncedAsyncSelect } from "../../../../../Utils/Hooks/useDebounce";
import { getErrorFromResponse } from "../../../../../../../utility/utils";
import { NotificationManager } from "react-notifications";

export const EVSForm = ({ idEVS, regresar }) => {
    const {
        getClientes,
        getProductosAProducir,
        getConsumidoresFinales,
        crearRegistroEVS,
        actualizarRegistroEVS,
        getDetalleRegistroEVS,
        cargando,
    } = CreacionEVSApi();

    const {
        control: formControl,
        register: formRegister,
        setValue: formSetValue,
        getValues: formGetValues,
        watch: formWatch,
        trigger: validateForm,
        formState: { errors },
    } = useForm({
        mode: "onChange",
    });

    const loadClientes = useDebouncedAsyncSelect(function (search) {
        return getClientes({ search: search });
    });

    const loadProductos = useDebouncedAsyncSelect(function (search) {
        return getProductosAProducir({ search: search });
    });

    const cliente = formWatch("cliente");

    const loadConsumidoresFinales = useDebouncedAsyncSelect(function (search) {
        if (!cliente) return Promise.resolve([]);

        return getConsumidoresFinales({
            search: search,
            cliente_id: cliente.id,
        }).then(function (consumidoresFinales) {
            return construirOpcionesConsumidorFinal(
                cliente,
                consumidoresFinales,
                search,
            );
        });
    });

    const FormSubmit = async () => {
        const isValid = await validateForm();
        if (!isValid) return;

        const formValues = formGetValues();

        const consumidorFinal = formValues.consumidor_final;

        const body = {
            numero: formValues.numero,
            cliente_id: formValues.cliente.id,
            producto_a_producir_id: formValues.producto_a_producir.id,
            tipo_consumidor_final: consumidorFinal.tipo,
            consumidor_final_id:
                consumidorFinal.tipo === TIPO_CONSUMIDOR_FINAL_ESPECIFICO
                    ? consumidorFinal.id
                    : null,
        };

        if (idEVS) {
            EditarEVS(body);
        } else {
            CrearEVS(body);
        }
    };

    const EditarEVS = async (body) => {
        try {
            await actualizarRegistroEVS(idEVS, body);
            NotificationManager.success(
                "EVS actualizado correctamente",
                "Éxito",
                3000,
            );
            regresar();
        } catch (error) {
            const errorMsg = getErrorFromResponse(
                error,
                "Error al actualizar EVS",
            );
            NotificationManager.error(errorMsg, "Error", 3000);
        }
    };

    const CrearEVS = async (body) => {
        try {
            await crearRegistroEVS(body);

            NotificationManager.success(
                "EVS creada correctamente",
                "Éxito",
                3000,
            );

            regresar();
        } catch (error) {
            const errorMsg = getErrorFromResponse(error, "Error al crear EVS");
            NotificationManager.error(errorMsg, "Error", 3000);
        }
    };

    const GetDetalleEVS = async () => {
        try {
            const data = await getDetalleRegistroEVS(idEVS);
            formSetValue("numero", data.numero);
            formSetValue("cliente", data.cliente);
            formSetValue("producto_a_producir", data.producto_a_producir);
            formSetValue("consumidor_final", data.consumidor_final);
        } catch (error) {
            const errorMsg = getErrorFromResponse(
                error,
                "Error al cargar detalle de EVS",
            );
            NotificationManager.error(errorMsg, "Error", 3000);
        }
    };

    useEffect(() => {
        if (idEVS) {
            GetDetalleEVS();
        }
    }, [idEVS]);

    return (
        <LoadMask loading={cargando} blur>
            <div
                className="d-flex justify-content-between flex-wrap align-items-end mb-3 mx-auto"
                style={{ maxWidth: "750px" }}
            >
                <h4 className="mb-0">Datos de EVS</h4>
                <div className="formulario-fields-row w-100 gap-1rem flex-wrap mb-0">
                    <div className="formulario-field">
                        <label className="text-nowrap">Número de EVS</label>
                        {/* INPUT DE TEXTO CON HOOK FORMS */}
                        <input
                            type="text"
                            {...formRegister("numero", {
                                required: "Este campo es obligatorio",
                            })}
                            className="form-control"
                            placeholder="Ejemplo: EVS-001"
                        />
                        {errors.numero && (
                            <small className="text-danger">
                                {errors.numero.message}
                            </small>
                        )}
                    </div>

                    <div className="formulario-field">
                        <label className="text-nowrap">Cliente</label>
                        <Controller
                            name="cliente"
                            control={formControl}
                            rules={{
                                required: "Debe seleccionar un cliente",
                            }}
                            render={({ field }) => (
                                <AsyncSelect
                                    {...field}
                                    className="react-select-container"
                                    isSearchable
                                    isClearable
                                    cacheOptions
                                    defaultOptions
                                    loadOptions={loadClientes}
                                    getOptionValue={(option) => option.id}
                                    getOptionLabel={(option) => option.nombre}
                                    onChange={(selected) => {
                                        field.onChange(selected);
                                        formSetValue("consumidor_final", null);
                                    }}
                                />
                            )}
                        />
                        {errors.cliente && (
                            <small className="text-danger">
                                {errors.cliente.message}
                            </small>
                        )}
                    </div>
                </div>
                <div className="formulario-fields-row w-100 gap-1rem flex-wrap">
                    <div className="formulario-field">
                        <label className="text-nowrap">
                            Producto a producir
                        </label>
                        <Controller
                            name="producto_a_producir"
                            control={formControl}
                            rules={{
                                required: "Debe seleccionar un producto",
                            }}
                            render={({ field }) => (
                                <AsyncSelect
                                    {...field}
                                    className="react-select-container"
                                    isSearchable
                                    isClearable
                                    cacheOptions
                                    defaultOptions
                                    loadOptions={loadProductos}
                                    getOptionValue={(option) => option.id}
                                    getOptionLabel={(option) => option.nombre}
                                    onChange={(selected) => {
                                        field.onChange(selected);
                                    }}
                                />
                            )}
                        />
                        {errors.producto_a_producir && (
                            <small className="text-danger">
                                {errors.producto_a_producir.message}
                            </small>
                        )}
                    </div>
                    <div className="formulario-field">
                        <label className="text-nowrap">Consumidor final</label>
                        <Controller
                            name="consumidor_final"
                            control={formControl}
                            rules={{
                                required: "Debe seleccionar un consumidor final",
                            }}
                            render={({ field }) => (
                                <AsyncSelect
                                    {...field}
                                    key={cliente ? cliente.id : "sin-cliente"}
                                    className="react-select-container"
                                    isSearchable
                                    isClearable
                                    cacheOptions
                                    defaultOptions
                                    isDisabled={!cliente}
                                    placeholder={
                                        cliente
                                            ? "Seleccionar..."
                                            : "Seleccione primero un cliente"
                                    }
                                    loadOptions={loadConsumidoresFinales}
                                    getOptionValue={(option) => option.id}
                                    getOptionLabel={(option) => option.nombre}
                                    onChange={(selected) => {
                                        field.onChange(selected);
                                    }}
                                />
                            )}
                        />
                        {errors.consumidor_final && (
                            <small className="text-danger">
                                {errors.consumidor_final.message}
                            </small>
                        )}
                    </div>
                </div>
                <div className="d-flex justify-content-center gap-1rem flex-wrap mt-5 w-100">
                    <button
                        type="button"
                        className="btn btn-outline-dark"
                        onClick={regresar}
                    >
                        Regresar
                    </button>
                    <button
                        type="button"
                        className="btn btn-primary"
                        onClick={FormSubmit}
                    >
                       Guardar
                    </button>
                </div>
            </div>
        </LoadMask>
    );
};
